'use client';

import { useState, useEffect, useRef, Children } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import blogs from '../../../../data/blogs';

export default function BlogGrid({
  searchQuery,
  selectedCategory,
  dateRange,
  setSearchQuery,
  setSelectedCategory,
  setDateRange,
}) {
  const [loading, setLoading] = useState(true);
  const [visibleBlogs, setVisibleBlogs] = useState(9);
  const [filteredBlogs, setFilteredBlogs] = useState(blogs);
  const [startDate, endDate] = dateRange;
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const gridRef = useRef(null);
  const isInView = useInView(gridRef, { once: false, amount: 0.1 });

  // Get unique categories from blogs
  const categories = [
    'All',
    ...new Set(
      blogs.map((blog) => {
        return blog.category
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }),
    ),
  ];

  const toTitleCase = (str) =>
    str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  useEffect(() => {
    // Filter blogs based on search, category, and date range
    const filtered = blogs
      .filter((blog) =>
        blog.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .filter(
        (blog) =>
          selectedCategory === 'All' ||
          toTitleCase(blog.category) === selectedCategory,
      )
      .filter((blog) => {
        if (!startDate || !endDate) return true;
        const blogDate = new Date(blog.date);
        return blogDate >= startDate && blogDate <= endDate;
      })
      .sort((a, b) => b.sort - a.sort)
      .slice(1);

    setFilteredBlogs(filtered);
  }, [searchQuery, selectedCategory, startDate, endDate]);

  const handleShowMore = () => {
    setVisibleBlogs((prev) => prev + 9);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSelectedCategory(category);
  };

  const highlightText = (text, search) => {
    if (!search || typeof text !== 'string') return text;
    const regex = new RegExp(`(${search})`, 'gi');
    return text.split(regex).map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <span key={index} className='bg-yellow-300 dark:bg-yellow-600'>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // Card variants for staggered animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Shimmer effect for cards
  const shimmer = {
    hidden: { x: '-100%', opacity: 0.1 },
    visible: {
      x: '100%',
      opacity: 0.3,
      transition: {
        repeat: Number.POSITIVE_INFINITY,
        duration: 1.5,
        ease: 'linear',
        repeatDelay: 0.5,
      },
    },
  };

  // Extract plain text from JSX description for preview
  // const getPlainTextPreview = (description, maxLength = 150) => {
  //   if (typeof description === 'string') {
  //     return description.length > maxLength
  //       ? description.substring(0, maxLength) + '...'
  //       : description;
  //   }

  //   // If it's a React element, try to extract text from its children
  //   let text = '';
  //   try {
  //     // Convert to string and strip HTML tags
  //     const stringVersion = JSON.stringify(description);
  //     text = stringVersion
  //       .replace(/<[^>]*>?/gm, '')
  //       .replace(/['"{}:,]/g, ' ')
  //       .replace(/\s+/g, ' ')
  //       .trim();

  //     return text.length > maxLength
  //       ? text.substring(0, maxLength) + '...'
  //       : text;
  //   } catch (e) {
  //     return 'Read more about this exciting update...';
  //   }
  // };

  const getTextFromJSX = (jsx) => {
    let text = '';
    Children.forEach(jsx.props.children, (child) => {
      if (typeof child === 'string') text += child;
      else if (child.props) text += getTextFromJSX(child);
    });
    return text;
  };

  const getPlainTextPreviewFromJSX = (jsx, maxLength = 150) => {
    const text = getTextFromJSX(jsx);
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  const formatDateForPost = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });

  return (
    <section className='mt-12' ref={gridRef}>
      {/* Category Pills */}
      {/* <motion.div
        className='mb-10 overflow-x-auto pb-4 flex space-x-2 no-scrollbar'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {categories.map((category) => (
          <motion.button
            key={category}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
              activeCategory === category
                ? 'bg-primarycolor text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
            onClick={() => handleCategoryChange(category)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {category}
          </motion.button>
        ))}
      </motion.div> */}

      <AnimatePresence mode='wait'>
        {filteredBlogs.length > 0 ? (
          <motion.div
            key='grid'
            variants={containerVariants}
            initial='hidden'
            animate={isInView ? 'visible' : 'hidden'}
          >
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'>
              {filteredBlogs.slice(0, visibleBlogs).map((blog, index) => (
                <motion.div
                  key={blog.slug}
                  variants={cardVariants}
                  className='group'
                  onHoverStart={() => setHoveredCard(blog.slug)}
                  onHoverEnd={() => setHoveredCard(null)}
                >
                  <Link
                    rel='preload'
                    href={`/blog/${blog.slug}`}
                    className='block h-full'
                  >
                    <motion.div
                      className='bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full flex flex-col relative'
                      whileHover={{
                        scale: 1.03,
                        transition: { duration: 0.3 },
                      }}
                      animate={{
                        y: hoveredCard === blog.slug ? -5 : 0,
                        boxShadow:
                          hoveredCard === blog.slug
                            ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                            : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Shimmer effect overlay */}
                      {hoveredCard === blog.slug && (
                        <motion.div
                          className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent z-10 pointer-events-none'
                          variants={shimmer}
                          initial='hidden'
                          animate='visible'
                        />
                      )}

                      {/* Image container */}
                      <div className='relative h-56 overflow-hidden'>
                        <Image
                          src={blog.images?.[0] || '/placeholder.jpg'}
                          alt={blog.image_alt || blog.title}
                          fill
                          className='object-cover transition-transform duration-700 group-hover:scale-110'
                          style={{
                            filter: loading ? 'blur(20px)' : 'none',
                            transition: '1s filter linear',
                          }}
                          priority
                          onLoad={() => setLoading(false)}
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                        />

                        {/* Date badge */}
                        <motion.div
                          className='absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 text-primarycolor dark:text-secondarycolor text-xs px-3 py-1 rounded-full z-20 backdrop-blur-sm'
                          whileHover={{ scale: 1.1 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 10,
                          }}
                        >
                          {formatDateForPost(blog.date)}
                        </motion.div>

                        {/* Category badge */}
                        <motion.div
                          className='absolute top-4 left-4 bg-primarycolor text-white text-xs px-3 py-1 rounded-full z-20'
                          whileHover={{ scale: 1.1 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 10,
                          }}
                        >
                          {blog.category
                            .split('_')
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(' ')}
                        </motion.div>

                        {/* Gradient overlay */}
                        <motion.div
                          className='absolute inset-0 bg-gradient-to-t from-black/50 to-transparent transition-opacity duration-300 z-10'
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: hoveredCard === blog.slug ? 1 : 0,
                          }}
                        />
                      </div>

                      {/* Content */}
                      <div className='p-6 flex flex-col flex-grow relative z-20'>
                        <motion.h3
                          className='text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 transition-colors'
                          animate={{
                            color:
                              hoveredCard === blog.slug
                                ? 'var(--primarycolor)'
                                : 'currentColor',
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          {highlightText(blog.title, searchQuery)}
                        </motion.h3>

                        <div className='text-gray-600 dark:text-gray-300 line-clamp-3 mb-4 flex-grow'>
                          {getPlainTextPreviewFromJSX(blog.description)}
                        </div>

                        {/* Content preview */}
                        {blog.contents && blog.contents.length > 0 && (
                          <div className='mt-2 mb-4'>
                            <div className='flex -space-x-2 overflow-hidden'>
                              {blog.contents.slice(0, 3).map(
                                (content, idx) =>
                                  content.image && (
                                    <div
                                      key={idx}
                                      className='inline-block h-8 w-8 rounded-full ring-2 ring-white dark:ring-gray-800 overflow-hidden'
                                    >
                                      <Image
                                        src={
                                          content.image || '/placeholder.svg'
                                        }
                                        alt={
                                          content.image_alt ||
                                          `Content ${idx + 1}`
                                        }
                                        width={32}
                                        height={32}
                                        className='h-full w-full object-cover'
                                        priority
                                      />
                                    </div>
                                  ),
                              )}
                              {blog.contents.length > 3 && (
                                <div className='flex items-center justify-center h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 ring-2 ring-white dark:ring-gray-800 text-xs font-medium text-gray-800 dark:text-gray-200'>
                                  +{blog.contents.length - 3}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className='flex items-center justify-between mt-auto'>
                          <motion.span
                            className='text-primarycolor dark:text-secondarycolor font-medium text-sm flex items-center gap-1'
                            initial={{ opacity: 0, x: -10 }}
                            animate={{
                              opacity: hoveredCard === blog.slug ? 1 : 0,
                              x: hoveredCard === blog.slug ? 0 : -10,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            Read More
                            <motion.svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              animate={{ x: hoveredCard === blog.slug ? 5 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <path d='M5 12h14'></path>
                              <path d='m12 5 7 7-7 7'></path>
                            </motion.svg>
                          </motion.span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Load more button */}
            {visibleBlogs < filteredBlogs.length && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className='flex justify-center mt-12'
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShowMore}
                  className='px-8 py-3 bg-primarycolor text-white rounded-lg hover:bg-secondarycolor transition-colors duration-300 shadow-md relative overflow-hidden group'
                >
                  <motion.span className='absolute inset-0 bg-gradient-to-r from-primarycolor to-secondarycolor opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
                  <span className='relative z-10'>Load More</span>

                  {/* Animated dots */}
                  <motion.span
                    className='ml-2 relative z-10'
                    animate={{
                      opacity: [1, 0.5, 1],
                    }}
                    transition={{
                      repeat: Number.POSITIVE_INFINITY,
                      duration: 1.5,
                      ease: 'easeInOut',
                    }}
                  >
                    ...
                  </motion.span>
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key='empty'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className='flex flex-col items-center justify-center py-16 px-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg'
          >
            <motion.svg
              className='w-16 h-16 text-gray-400 mb-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 3,
                ease: 'easeInOut',
              }}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              ></path>
            </motion.svg>
            <h3 className='text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2'>
              No Blogs Found
            </h3>
            <p className='text-gray-500 dark:text-gray-400 text-center max-w-md mb-6'>
              We couldn't find any blogs matching your search criteria. Try
              adjusting your filters or search query.
            </p>
            {/* <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
                setDateRange([null, null]);
              }}
              className='px-6 py-2 bg-primarycolor text-white rounded-lg hover:bg-secondarycolor transition-colors duration-300'
            >
              Reset Filters
            </motion.button> */}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
